import { Action } from 'redux';
import * as actions from './actions';
import { LoadingAction } from '../common/loading-action';
import { ErrorAction } from '../common/error-action';
import { NewOwnership } from '@services/ownership/types/new-ownership';
import { Ownership } from '@services/ownership/types/ownership';
import { SplitUpdate } from '@services/ownership/types/split-update';
import { ValidOwnershipType } from '@services/ownership/types/valid-ownership-type';
import { SplitUpdateRequest } from '@services/ownership/types/split-update-request';

export class CreateOwnership implements LoadingAction {
  readonly type = actions.CREATE_OWNERSHIP;
  readonly isLoading = true;

  constructor(
    public ownership: NewOwnership,
    public isReplicatingFromFirstTrack?: boolean
  ) {}
}

export class CreateOwnershipSuccess implements LoadingAction {
  readonly type = actions.CREATE_OWNERSHIP_SUCCESS;
  readonly isLoading = false;

  constructor(
    public ownership: Ownership,
    public isReplicatingFromFirstTrack?: boolean
  ) {}
}

export class CreateOwnershipError implements ErrorAction {
  readonly type = actions.CREATE_OWNERSHIP_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateOwnership implements LoadingAction {
  readonly type = actions.UPDATE_OWNERSHIP;
  readonly isLoading = true;

  constructor(public ownership: Ownership) {}
}

export class UpdateOwnershipSuccess implements LoadingAction {
  readonly type = actions.UPDATE_OWNERSHIP_SUCCESS;
  readonly isLoading = false;

  constructor(public ownership: Ownership) {}
}

export class UpdateOwnershipError implements ErrorAction {
  readonly type = actions.UPDATE_OWNERSHIP_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateOwnershipOrder implements LoadingAction {
  readonly type = actions.UPDATE_OWNERSHIP_ORDER;
  readonly isLoading = true;

  constructor(public assetId: string, public newOrder: string[]) {}
}

export class UpdateOwnershipOrderSuccess implements LoadingAction {
  readonly type = actions.UPDATE_OWNERSHIP_ORDER_SUCCESS;
  readonly isLoading = false;

  constructor(public assetId: string, public newOrder: string[]) {}
}

export class UpdateOwnershipOrderError implements ErrorAction {
  readonly type = actions.UPDATE_OWNERSHIP_ORDER_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class ReassignSplits implements LoadingAction {
  readonly type = actions.REASSIGN_SPLITS;
  readonly isLoading = true;

  constructor(
    public assetId: string,
    public ownershipType: ValidOwnershipType,
    public newSplits: SplitUpdate[],
    public newOwnerShips: Ownership[] | [],
    public showSuccessNotification?: boolean
  ) {}
}

export class ReassignSplitsSuccess implements LoadingAction {
  readonly type = actions.REASSIGN_SPLITS_SUCCESS;
  readonly isLoading = false;

  constructor(
    public newSplits: SplitUpdateRequest,
    public showToastNotification?: boolean,
    public isUnsaved?: boolean
  ) {}
}

export class ReassignSplitsError implements ErrorAction {
  readonly type = actions.REASSIGN_SPLITS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class GetOwnerships implements LoadingAction {
  readonly type = actions.GET_OWNERSHIPS;
  readonly isLoading = true;

  constructor(public assetId: string) {}
}

export class GetOwnershipsSuccess implements LoadingAction {
  readonly type = actions.GET_OWNERSHIPS_SUCCESS;
  readonly isLoading = false;

  constructor(public ownerships: Ownership[]) {}
}

export class GetOwnershipsError implements ErrorAction {
  readonly type = actions.GET_OWNERSHIPS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class GetOwnershipsByUserId implements LoadingAction {
  readonly type = actions.GET_OWNERSHIPS_BY_USER_ID;
  readonly isLoading = true;

  constructor(public userId: string) {}
}

export class GetOwnershipsByUserIdSuccess implements LoadingAction {
  readonly type = actions.GET_OWNERSHIPS_BY_USER_ID_SUCCESS;
  readonly isLoading = false;

  constructor(public userOwnerships: Ownership[]) {}
}

export class GetOwnershipsByUserIdError implements ErrorAction {
  readonly type = actions.GET_OWNERSHIPS_BY_USER_ID_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateOwnershipField<T extends keyof Ownership> implements Action {
  readonly type = actions.UPDATE_OWNERSHIP_FIELD;

  constructor(
    public assetId: string,
    public ownershipId: string,
    public field: T,
    public value: Ownership[T]
  ) {}
}

export class UpdateRequestOwnershipField implements LoadingAction {
  readonly type = actions.UPDATE_REQUEST_OWNERSHIP_FIELD;
  readonly isLoading = true;

  constructor(
    public assetId: string,
    public ownershipId: string,
    public field: 'split',
    public value: Ownership['split']
  ) {}
}

export class UpdateRequestOwnershipFieldSuccess implements LoadingAction {
  readonly type = actions.UPDATE_REQUEST_OWNERSHIP_FIELD_SUCCESS;
  readonly isLoading = false;

  constructor(public newSplits: SplitUpdate[]) {}
}

export class UpdateRequestOwnershipFieldError implements ErrorAction {
  readonly type = actions.UPDATE_REQUEST_OWNERSHIP_FIELD_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class RemoveOwnership implements LoadingAction {
  readonly type = actions.REMOVE_OWNERSHIP;
  readonly isLoading = true;

  constructor(public assetId: string, public ownershipId: string) {}
}

export class RemoveOwnershipSuccess implements LoadingAction {
  readonly type = actions.REMOVE_OWNERSHIP_SUCCESS;
  readonly isLoading = false;

  constructor(public assetId: string, public ownershipId: string) {}
}

export class RemoveOwnershipError implements ErrorAction {
  readonly type = actions.REMOVE_OWNERSHIP_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class RemoveManagerOwnership implements LoadingAction {
  readonly type = actions.REMOVE_MANAGER_OWNERSHIP;
  readonly isLoading = true;

  constructor(
    public artistId: string,
    public trackOwnerShip: any,
    public merchOwnership: any,
    public removeOwnership: string
  ) {}
}

export class RemoveManagerOwnershipSuccess implements LoadingAction {
  readonly type = actions.REMOVE_MANAGER_OWNERSHIP_SUCCESS;
  readonly isLoading = false;

  constructor(
    public artistId: string,
    public trackOwnerShip: any,
    public merchOwnership: any,
    public removeOwnership: string
  ) {}
}

export class RemoveManagerOwnershipError implements ErrorAction {
  readonly type = actions.REMOVE_MANAGER_OWNERSHIP_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class ClearOwnershipState implements Action {
  readonly type = actions.CLEAR_OWNERSHIP_STATE;
}

export type OwnershipActions =
  | CreateOwnership
  | CreateOwnershipSuccess
  | CreateOwnershipError
  | GetOwnerships
  | GetOwnershipsSuccess
  | GetOwnershipsError
  | GetOwnershipsByUserId
  | GetOwnershipsByUserIdSuccess
  | GetOwnershipsByUserIdError
  | UpdateOwnershipField<keyof Ownership>
  | UpdateRequestOwnershipField
  | UpdateRequestOwnershipFieldSuccess
  | UpdateRequestOwnershipFieldError
  | UpdateOwnership
  | UpdateOwnershipSuccess
  | UpdateOwnershipError
  | UpdateOwnershipOrder
  | UpdateOwnershipOrderSuccess
  | UpdateOwnershipOrderError
  | ReassignSplits
  | ReassignSplitsSuccess
  | ReassignSplitsError
  | RemoveOwnership
  | RemoveOwnershipSuccess
  | RemoveOwnershipError
  | RemoveManagerOwnership
  | RemoveManagerOwnershipSuccess
  | RemoveManagerOwnershipError
  | ClearOwnershipState;
